import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const OBLIGATION_TYPES = [
  { value: 'MORTGAGE', label: 'Mortgage', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  { value: 'INSURANCE', label: 'Insurance', color: 'text-cyan-300', bg: 'bg-cyan-400/10 border-cyan-400/20' },
  { value: 'PROPERTY_TAX', label: 'Property Tax', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  { value: 'MAINTENANCE', label: 'Maintenance', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
  { value: 'HOA_FEE', label: 'HOA Fee', color: 'text-teal-400', bg: 'bg-teal-500/10 border-teal-500/20' },
  { value: 'OTHER', label: 'Other', color: 'text-white/50', bg: 'bg-white/5 border-white/10' },
];

const typeInfo = (type: string) =>
  OBLIGATION_TYPES.find(t => t.value === type) || OBLIGATION_TYPES[OBLIGATION_TYPES.length - 1];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface DebtObligation {
  id: string;
  name: string;
  type: string;
  amount: number;
  addressId?: string | null;
  address?: { id: string; name: string; city: string; state: string } | null;
  notes?: string | null;
}

interface PerProperty {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  tenant: { name: string } | null;
  sharedTenant: boolean;
  monthlyRent: number;
  monthlyObligations: number;
  monthlyExpenses: number;          // direct (property-tied) expenses for current month
  netCashFlow: number;
  obligations: { id: string; name: string; type: string; amount: number }[];
}

interface MonthlyBreakdown {
  month: number;
  collected: number;
  obligations: number;
  expenses: number;
  net: number;
}

interface GeneralExpense {
  id: string;
  description: string;
  category: string;
  amount: number;
  date: string;
}

interface CashFlowSummary {
  monthlyIncome: number;
  monthlyObligations: number;
  monthlyExpenses: number;          // total expenses this month (per-property + general)
  generalMonthExpenses: number;     // portfolio-wide expenses this month only
  generalMonthExpenseList: GeneralExpense[];
  netMonthlyCashFlow: number;
  annualProjection: number;
  annualIncome: number;
  annualObligations: number;
  annualExpensesYTD: number;        // year-to-date actual expenses
  perProperty: PerProperty[];
  portfolioObligations: (DebtObligation & { amount: number })[];
  monthlyBreakdown: MonthlyBreakdown[];
  occupiedUnits: number;
  totalUnits: number;
}

interface PropertyOption {
  id: string;
  name: string;
  city: string;
  state: string;
}

interface ObligationForm {
  name: string;
  type: string;
  amount: string;
  addressId: string;
  notes: string;
}

const emptyForm: ObligationForm = { name: '', type: 'MORTGAGE', amount: '', addressId: '', notes: '' };

const CashFlow: React.FC = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState<CashFlowSummary | null>(null);
  const [obligations, setObligations] = useState<DebtObligation[]>([]);
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'obligations' | 'annual'>('overview');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ObligationForm>(emptyForm);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const headers = useCallback(() => {
    const g = localStorage.getItem('selectedPropertyGroup');
    return g ? { 'x-selected-group': g } : {};
  }, []);

  const fetchAll = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const [summaryRes, obligRes, propRes] = await Promise.all([
        api.get<CashFlowSummary>('/api/debt-obligations/cash-flow', { headers: headers() }),
        api.get<DebtObligation[]>('/api/debt-obligations', { headers: headers() }),
        api.get<PropertyOption[]>('/api/addresses', { headers: headers() }),
      ]);
      setSummary(summaryRes.data);
      setObligations(obligRes.data);
      setProperties(propRes.data);
    } catch (e) {
      console.error('Error loading cash flow data:', e);
      setMessage('Failed to load cash flow data');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }, [user?.token, headers]);

  useEffect(() => {
    if (user?.role === 'ADMIN') fetchAll();
    else setLoading(false);
  }, [fetchAll, user?.role]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (o: DebtObligation) => {
    setEditingId(o.id);
    setForm({
      name: o.name,
      type: o.type,
      amount: String(o.amount),
      addressId: o.addressId || '',
      notes: o.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.name || !form.type || !form.amount) {
      setMessage('Name, type, and amount are required');
      setMessageType('error');
      return;
    }
    try {
      const payload = {
        name: form.name,
        type: form.type,
        amount: parseFloat(form.amount),
        addressId: form.addressId || null,
        notes: form.notes || null
      };
      if (editingId) {
        await api.put(`/api/debt-obligations/${editingId}`, payload, { headers: headers() });
        setMessage('Obligation updated');
      } else {
        await api.post('/api/debt-obligations', payload, { headers: headers() });
        setMessage('Obligation added');
      }
      setMessageType('success');
      closeModal();
      await fetchAll();
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Failed to save obligation');
      setMessageType('error');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/api/debt-obligations/${id}`, { headers: headers() });
      setMessage('Obligation deleted');
      setMessageType('success');
      await fetchAll();
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Failed to delete');
      setMessageType('error');
    }
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

  if (user?.role !== 'ADMIN') {
    return (
      <div className="text-center py-8">
        <p className="text-white/60">Admin access required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#22d3ee] mx-auto"></div>
        <p className="text-white/60 mt-2">Calculating cash flow…</p>
      </div>
    );
  }

  const net = summary?.netMonthlyCashFlow ?? 0;
  const isCashFlowing = net >= 0;
  const currentMonth = new Date().getMonth();

  return (
    <div className="space-y-8">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Cash Flow</h1>
          <p className="text-white/50 mt-1">Portfolio income vs. debt obligations</p>
        </div>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-5 h-5" />
          Add Obligation
        </button>
      </div>

      {/* ── Messages ── */}
      {message && (
        <div className={`p-4 rounded-xl border ${messageType === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
          {message}
        </div>
      )}

      {/* ── Cash Flow Health Banner ── */}
      <div className={`rounded-2xl p-5 border flex items-center gap-4 ${
        isCashFlowing
          ? 'bg-green-500/8 border-green-500/20'
          : 'bg-red-500/8 border-red-500/25'
      }`}>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
          isCashFlowing ? 'bg-green-500/20' : 'bg-red-500/20'
        }`}>
          {isCashFlowing
            ? <ArrowTrendingUpIcon className="w-6 h-6 text-green-400" />
            : <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
          }
        </div>
        <div>
          <p className={`text-lg font-bold ${isCashFlowing ? 'text-green-400' : 'text-red-400'}`}>
            {isCashFlowing ? `Cash flowing — ${fmt(net)}/mo positive` : `Negative cash flow — ${fmt(Math.abs(net))}/mo shortfall`}
          </p>
          <p className="text-sm text-white/50">
            {summary?.occupiedUnits ?? 0} of {summary?.totalUnits ?? 0} units occupied •{' '}
            {isCashFlowing
              ? `Projected ${fmt((summary?.annualProjection ?? 0))} net for the year`
              : `Annual loss of ${fmt(Math.abs(summary?.annualProjection ?? 0))} if unchanged`
            }
          </p>
        </div>
      </div>

      {/* ── Summary Stats ── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="floating-card text-center py-5">
          <p className="text-2xl font-bold text-green-400">{fmt(summary?.monthlyIncome ?? 0)}</p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">Monthly Income</p>
          <p className="text-xs text-white/30 mt-0.5">{fmt((summary?.annualIncome ?? 0))} / yr</p>
        </div>
        <div className="floating-card text-center py-5">
          <p className="text-2xl font-bold text-red-400">{fmt(summary?.monthlyObligations ?? 0)}</p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">Obligations</p>
          <p className="text-xs text-white/30 mt-0.5">recurring monthly</p>
        </div>
        <div className="floating-card text-center py-5">
          <p className="text-2xl font-bold text-amber-400">{fmt(summary?.monthlyExpenses ?? 0)}</p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">Expenses</p>
          <p className="text-xs text-white/30 mt-0.5">{fmt(summary?.annualExpensesYTD ?? 0)} YTD</p>
        </div>
        <div className="floating-card text-center py-5">
          <p className={`text-2xl font-bold ${isCashFlowing ? 'text-green-400' : 'text-red-400'}`}>
            {net >= 0 ? '+' : ''}{fmt(net)}
          </p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">Net / Month</p>
        </div>
        <div className="floating-card text-center py-5">
          <p className={`text-2xl font-bold ${(summary?.annualProjection ?? 0) >= 0 ? 'text-[#22d3ee]' : 'text-red-400'}`}>
            {(summary?.annualProjection ?? 0) >= 0 ? '+' : ''}{fmt(summary?.annualProjection ?? 0)}
          </p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-wide">Annual Projection</p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center bg-white/5 rounded-xl p-1 border border-white/10 w-fit">
        {(['overview', 'obligations', 'annual'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${
              activeTab === tab ? 'bg-[#22d3ee] text-white' : 'text-white/50 hover:text-white'
            }`}
          >
            {tab === 'overview' ? 'Per Property' : tab === 'obligations' ? 'Obligations' : 'Annual View'}
          </button>
        ))}
      </div>

      {/* ══════════════════ PER-PROPERTY TAB ══════════════════ */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {(summary?.perProperty ?? []).length === 0 ? (
            <div className="floating-card text-center py-12">
              <BuildingOffice2Icon className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/50">No properties found. Add properties and assign tenant rents to see cash flow.</p>
            </div>
          ) : (
            (summary?.perProperty ?? []).map(prop => {
              const positive = prop.netCashFlow >= 0;
              return (
                <div key={prop.id} className="floating-card">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Property info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-[#22d3ee]/15 flex items-center justify-center flex-shrink-0">
                        <BuildingOffice2Icon className="w-5 h-5 text-[#22d3ee]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-white truncate">{prop.name}</h3>
                        <p className="text-xs text-white/40">{prop.city}, {prop.state}</p>
                        {prop.tenant ? (
                          <p className="text-xs text-white/60 mt-0.5">
                            Tenant: {prop.tenant.name}
                            {prop.sharedTenant && (
                              <span className="ml-2 text-[10px] bg-blue-500/15 text-blue-400 border border-blue-500/20 rounded px-1.5 py-0.5">
                                multi-unit · rent counted once
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="text-xs text-amber-400/70 mt-0.5">Vacant — no rent income</p>
                        )}
                      </div>
                    </div>

                    {/* Financial breakdown */}
                    <div className="flex items-center gap-4 sm:gap-5 flex-shrink-0 flex-wrap">
                      <div className="text-center">
                        <p className="text-sm font-semibold text-green-400">{fmt(prop.monthlyRent)}</p>
                        <p className="text-[10px] text-white/40 uppercase tracking-wide">Rent</p>
                      </div>
                      <div className="text-white/20 text-lg">−</div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-red-400">{fmt(prop.monthlyObligations)}</p>
                        <p className="text-[10px] text-white/40 uppercase tracking-wide">Oblig.</p>
                      </div>
                      <div className="text-white/20 text-lg">−</div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-amber-400">{fmt(prop.monthlyExpenses)}</p>
                        <p className="text-[10px] text-white/40 uppercase tracking-wide">Expenses</p>
                      </div>
                      <div className="text-white/20 text-lg">=</div>
                      <div className="text-center">
                        <p className={`text-base font-bold ${positive ? 'text-green-400' : 'text-red-400'}`}>
                          {prop.netCashFlow >= 0 ? '+' : ''}{fmt(prop.netCashFlow)}
                        </p>
                        <p className="text-[10px] text-white/40 uppercase tracking-wide">Net/mo</p>
                      </div>
                    </div>
                  </div>

                  {/* Obligations breakdown for this property */}
                  {prop.obligations.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-2">
                      {prop.obligations.map(o => {
                        const ti = typeInfo(o.type);
                        return (
                          <span key={o.id} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border ${ti.bg} ${ti.color}`}>
                            {ti.label}: {fmt(o.amount)}/mo
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Portfolio-level obligations */}
          {(summary?.portfolioObligations ?? []).length > 0 && (
            <div className="floating-card">
              <h3 className="text-sm font-semibold text-white/60 mb-3 uppercase tracking-wide">Portfolio-Level Obligations</h3>
              <div className="flex flex-wrap gap-2">
                {(summary?.portfolioObligations ?? []).map(o => {
                  const ti = typeInfo(o.type);
                  return (
                    <span key={o.id} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-xl border ${ti.bg} ${ti.color}`}>
                      {o.name}: {fmt(o.amount)}/mo
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* General (portfolio-wide) expenses */}
          {(summary?.generalMonthExpenses ?? 0) > 0 && (
            <div className="floating-card border-amber-500/20">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wide flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  General Expenses — This Month
                </h3>
                <p className="text-base font-bold text-amber-400">{fmt(summary?.generalMonthExpenses ?? 0)}</p>
              </div>
              <p className="text-xs text-white/40 mb-3">Portfolio-wide costs not tied to a single unit. Counted in totals but not in per-property net.</p>
              <div className="space-y-1.5">
                {(summary?.generalMonthExpenseList ?? []).map(e => (
                  <div key={e.id} className="flex items-center justify-between text-sm bg-white/3 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-white/30 flex-shrink-0">
                        {new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <span className="text-white truncate">{e.description}</span>
                      <span className="text-xs text-white/40 flex-shrink-0">· {e.category.replace(/_/g, ' ').toLowerCase()}</span>
                    </div>
                    <span className="text-amber-400 font-medium ml-3 flex-shrink-0">{fmt(e.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════ OBLIGATIONS TAB ══════════════════ */}
      {activeTab === 'obligations' && (
        <div className="space-y-4">
          {obligations.length === 0 ? (
            <div className="floating-card text-center py-12">
              <CurrencyDollarIcon className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/50 mb-4">No debt obligations recorded yet.</p>
              <button onClick={openAdd} className="btn-primary">Add Your First Obligation</button>
            </div>
          ) : (
            obligations.map(o => {
              const ti = typeInfo(o.type);
              return (
                <div key={o.id} className="floating-card flex items-center gap-4">
                  {/* Type badge */}
                  <span className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border whitespace-nowrap ${ti.bg} ${ti.color}`}>
                    {ti.label}
                  </span>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{o.name}</p>
                    {o.address ? (
                      <p className="text-xs text-white/40">{o.address.name} · {o.address.city}, {o.address.state}</p>
                    ) : (
                      <p className="text-xs text-white/40">Portfolio-level</p>
                    )}
                    {o.notes && <p className="text-xs text-white/30 mt-0.5 italic">{o.notes}</p>}
                  </div>

                  {/* Amount */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-base font-bold text-red-400">{fmt(o.amount)}</p>
                    <p className="text-xs text-white/40">/ month</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEdit(o)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4 text-white/40 hover:text-white" />
                    </button>
                    <button
                      onClick={() => handleDelete(o.id, o.name)}
                      className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4 text-white/40 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {obligations.length > 0 && (
            <button onClick={openAdd} className="w-full py-3 rounded-xl border border-dashed border-white/15 hover:border-[#22d3ee]/40 hover:bg-[#22d3ee]/5 transition-all text-sm text-white/35 hover:text-[#22d3ee] flex items-center justify-center gap-2">
              <PlusIcon className="w-4 h-4" />
              Add Another Obligation
            </button>
          )}
        </div>
      )}

      {/* ══════════════════ ANNUAL VIEW TAB ══════════════════ */}
      {activeTab === 'annual' && (
        <div className="space-y-6">

          {/* Annual totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="floating-card text-center py-5">
              <p className="text-xs text-white/40 uppercase tracking-wide mb-2">Annual Income</p>
              <p className="text-2xl font-bold text-green-400">{fmt(summary?.annualIncome ?? 0)}</p>
              <p className="text-xs text-white/30 mt-1">({fmt(summary?.monthlyIncome ?? 0)}/mo × 12)</p>
            </div>
            <div className="floating-card text-center py-5">
              <p className="text-xs text-white/40 uppercase tracking-wide mb-2">Annual Obligations</p>
              <p className="text-2xl font-bold text-red-400">{fmt(summary?.annualObligations ?? 0)}</p>
              <p className="text-xs text-white/30 mt-1">({fmt(summary?.monthlyObligations ?? 0)}/mo × 12)</p>
            </div>
            <div className="floating-card text-center py-5">
              <p className="text-xs text-white/40 uppercase tracking-wide mb-2">Expenses YTD</p>
              <p className="text-2xl font-bold text-amber-400">{fmt(summary?.annualExpensesYTD ?? 0)}</p>
              <p className="text-xs text-white/30 mt-1">actual to date</p>
            </div>
            <div className={`floating-card text-center py-5 ${(summary?.annualProjection ?? 0) >= 0 ? 'border-green-500/25' : 'border-red-500/25'}`}>
              <p className="text-xs text-white/40 uppercase tracking-wide mb-2">Net Annual (NOI)</p>
              <p className={`text-2xl font-bold ${(summary?.annualProjection ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(summary?.annualProjection ?? 0) >= 0 ? '+' : ''}{fmt(summary?.annualProjection ?? 0)}
              </p>
              <p className="text-xs text-white/30 mt-1">
                {(summary?.annualProjection ?? 0) >= 0 ? 'Positive cash flow' : 'Review obligations & expenses'}
              </p>
            </div>
          </div>

          {/* Month-by-month chart */}
          <div className="floating-card">
            <h3 className="text-base font-semibold text-white mb-5">
              Monthly Breakdown — {new Date().getFullYear()}
            </h3>

            <div className="space-y-2">
              {(summary?.monthlyBreakdown ?? []).map((m) => {
                const isCurrentMonth = m.month === currentMonth;
                const isFuture = m.month > currentMonth;
                // Future months: project income − recurring obligations only (no expenses, since
                // expenses are one-time/unpredictable). This keeps the bars and the net value
                // visually consistent (the "Exp." bar shows 0, and net excludes expenses).
                const collectedDisplay = isFuture ? (summary?.monthlyIncome ?? 0) : m.collected;
                const expensesDisplay = isFuture ? 0 : m.expenses;
                const netVal = isFuture
                  ? (summary?.monthlyIncome ?? 0) - (summary?.monthlyObligations ?? 0)
                  : m.net;
                const positive = netVal >= 0;
                const maxVal = Math.max(
                  ...(summary?.monthlyBreakdown ?? []).map(x => Math.max(x.collected, x.obligations, x.expenses, 1))
                );
                const barWidth = (val: number) => `${Math.max(2, (Math.abs(val) / maxVal) * 100)}%`;

                return (
                  <div key={m.month} className={`rounded-xl p-3 transition-colors ${isCurrentMonth ? 'bg-[#22d3ee]/10 border border-[#22d3ee]/25' : 'hover:bg-white/3'}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 text-xs text-white/50 font-medium flex-shrink-0">
                        {MONTHS[m.month]}
                        {isCurrentMonth && <div className="text-[9px] text-[#22d3ee]">NOW</div>}
                      </div>

                      <div className="flex-1 space-y-1">
                        {/* Income bar */}
                        <div className="flex items-center gap-2">
                          <div className="w-12 text-xs text-white/30 text-right flex-shrink-0">In</div>
                          <div className="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${isFuture ? 'bg-green-500/40' : 'bg-green-500'}`}
                              style={{ width: barWidth(collectedDisplay) }}
                            />
                          </div>
                          <div className={`text-xs w-16 text-right flex-shrink-0 ${isFuture ? 'text-green-400/50' : 'text-green-400'}`}>
                            {fmt(collectedDisplay)}
                          </div>
                        </div>
                        {/* Obligations bar */}
                        <div className="flex items-center gap-2">
                          <div className="w-12 text-xs text-white/30 text-right flex-shrink-0">Oblig.</div>
                          <div className="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-red-500/60"
                              style={{ width: barWidth(m.obligations) }}
                            />
                          </div>
                          <div className="text-xs text-red-400/70 w-16 text-right flex-shrink-0">
                            {fmt(m.obligations)}
                          </div>
                        </div>
                        {/* Expenses bar */}
                        <div className="flex items-center gap-2">
                          <div className="w-12 text-xs text-white/30 text-right flex-shrink-0">Exp.</div>
                          <div className="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-amber-500/60"
                              style={{ width: barWidth(expensesDisplay) }}
                            />
                          </div>
                          <div className={`text-xs w-16 text-right flex-shrink-0 ${isFuture ? 'text-amber-400/30' : 'text-amber-400/80'}`}>
                            {fmt(expensesDisplay)}
                          </div>
                        </div>
                      </div>

                      {/* Net */}
                      <div className={`w-16 text-right flex-shrink-0 text-sm font-bold ${positive ? 'text-green-400' : 'text-red-400'} ${isFuture ? 'opacity-40' : ''}`}>
                        {netVal >= 0 ? '+' : ''}{fmt(netVal)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10 text-xs text-white/30 flex-wrap">
              <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-green-500 inline-block" /> Collected rent (past) / projected (future)</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-red-500/60 inline-block" /> Recurring obligations</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-amber-500/60 inline-block" /> One-time expenses</span>
            </div>
          </div>

          {/* Cash flow health tips */}
          {!isCashFlowing && (
            <div className="floating-card border-red-500/20">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-400 mb-2">Negative Cash Flow — Action Items</p>
                  <ul className="text-sm text-white/60 space-y-1 list-disc list-inside">
                    {(summary?.totalUnits ?? 0) > (summary?.occupiedUnits ?? 0) && (
                      <li>{(summary?.totalUnits ?? 0) - (summary?.occupiedUnits ?? 0)} unit(s) are vacant — filling them adds {fmt(((summary?.totalUnits ?? 0) - (summary?.occupiedUnits ?? 0)) * ((summary?.monthlyIncome ?? 0) / Math.max(1, summary?.occupiedUnits ?? 1)))}/mo estimated income</li>
                    )}
                    <li>Review obligations — consider refinancing high mortgage payments</li>
                    <li>Shortfall is {fmt(Math.abs(net))}/mo — adjust rent amounts to close the gap</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════ ADD / EDIT MODAL ══════════════════ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1b] border border-white/10 rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">
                {editingId ? 'Edit Obligation' : 'Add Debt Obligation'}
              </h3>
              <button onClick={closeModal} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <XMarkIcon className="w-5 h-5 text-white/60" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Obligation Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="input-field"
                  placeholder="e.g. Mortgage – Unit 4B, Portfolio Insurance"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Type *</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="input-field"
                >
                  {OBLIGATION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Monthly Amount ($) *</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  className="input-field"
                  placeholder="e.g. 1450"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Linked Property <span className="text-white/30">(optional)</span></label>
                <select
                  value={form.addressId}
                  onChange={e => setForm(f => ({ ...f, addressId: e.target.value }))}
                  className="input-field"
                >
                  <option value="">Portfolio-level (not tied to one unit)</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.name} — {p.city}, {p.state}</option>
                  ))}
                </select>
                <p className="text-xs text-white/30 mt-1">Link to a specific unit for per-property cash flow analysis.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Notes <span className="text-white/30">(optional)</span></label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="input-field"
                  placeholder="e.g. 30-yr fixed at 6.5%, due 1st of month"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={closeModal} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleSave} className="btn-primary flex-1 flex items-center justify-center gap-2">
                <CheckIcon className="w-4 h-4" />
                {editingId ? 'Save Changes' : 'Add Obligation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashFlow;
